import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Layers,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { TopTabs } from '@/components/dashboard/TopTabs'
import { EventsSubTabs } from '@/components/events/EventsSubTabs'
import { DISCIPLINE_LABELS } from '@/components/events/EventFormModal'
import { WeeklyTrendChart } from '@/components/events/WeeklyTrendChart'
import { ChannelTrendChart } from '@/components/events/ChannelTrendChart'
import { OfflineSalesPanel } from '@/components/events/OfflineSalesPanel'
import { MetricCard } from '@/components/shared/MetricCard'
import { MarginCell, SellThrough, SoldOutPill } from '@/components/events/SellThrough'
import { SELL_THROUGH_TEXT, sellThroughLevel } from '@/lib/sellThrough'
import {
  useChannelAnalysis,
  useEventAnalysis,
  useEventLocations,
  useEvents,
  useSizeCurve,
  useWeeklyTrend,
  type AnalysisProduct,
  type AnalysisVariant,
  type EventChannel,
  type EventResponse,
} from '@/hooks/useEvents'
import { fmtDate, fmtMonth } from '@/lib/dates'

const money = (v: number) =>
  v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

// Units sold outside POS. Kept beside — not inside — the sold figure, which
// (like sell-through) stays POS-only against the stock that was brought.
function OfflineQty({ qty }: { qty: number | undefined }) {
  if (!qty) return null
  return <span className="ml-1 font-normal text-[var(--mute)]">+{qty} offline</span>
}

// PRODUCT-level filter: the discipline tag carried by the product in Shopify.
// Distinct from the EVENT discipline select, which picks which event weeks are
// in scope.
const DISCIPLINE_FILTERS = [
  { value: '', label: 'All product tags' },
  { value: 'hunter_jumper', label: 'Hunter-Jumper tag' },
  { value: 'dressage', label: 'Dressage tag' },
  { value: 'none', label: 'No tag' },
]

// Click-to-sort applies at every drill level (categories, then products and
// variants inside their parent). Text sorts A→Z first; numbers high→low
// first; third click restores the default units-sold order.
type SortKey = 'name' | 'qty_available' | 'qty_sold' | 'sell_through_pct' | 'revenue' | 'margin_pct' | 'missed'

const SORT_COLUMNS: { key: SortKey; label: string; numeric: boolean; tooltip?: string }[] = [
  { key: 'name', label: 'Category / Product / Variant', numeric: false },
  { key: 'qty_available', label: 'Avail.', numeric: true, tooltip: 'Available = carryover from before this event week + transferred this week' },
  { key: 'qty_sold', label: 'Sold', numeric: true },
  { key: 'sell_through_pct', label: 'Sell-through', numeric: true },
  { key: 'revenue', label: 'Revenue', numeric: true },
  { key: 'margin_pct', label: 'Margin %', numeric: true },
  { key: 'missed', label: 'Missed rev.', numeric: true },
]

// A channel has no pack list, so the same three columns mean something else:
// the store stock pool, not a transfer denominator. Both channels share the
// Brewster pool — Online sells out of the same shelves.
const CHANNEL_COLUMN_TOOLTIPS: Partial<Record<SortKey, string>> = {
  qty_available:
    "On hand at the store at period start + units restocked during the period (receipts, returns and transfers back from events). Online shares the store's stock pool." +
    ' Product rows count every size we stock, sold or not; category rollups cover only products that sold at least one unit.',
  sell_through_pct: 'Share of the available pool sold through this channel',
  missed: 'Estimated revenue lost on days the item was out of stock',
}

// Brewster Store and Online aren't events, but they sit in the same venue
// dropdown so Gina can compare them against event weeks without switching page.
const CHANNEL_LABELS: Record<EventChannel, string> = {
  brewster: 'Brewster Store',
  online: 'Online',
  all: 'All Locations',
}

// The venue select already spends "all" on "All venues" (event mode), so the
// All Locations channel rides a distinct option value.
const ALL_LOCATIONS_VALUE = 'channel-all'

const CHANNEL_WEEK_COUNT = 12
const CHANNEL_MONTH_COUNT = 13

// Week starts are Mondays in local time — the same calendar week the store
// staff mean when they say "last week".
function isoDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

function recentWeekStarts(count: number): string[] {
  const now = new Date()
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7))
  const weeks: string[] = []
  for (let i = 0; i < count; i++) {
    weeks.push(isoDate(monday))
    monday.setDate(monday.getDate() - 7)
  }
  return weeks // newest first
}

// Calendar months, newest first, as 'YYYY-MM'. Months and weeks live in the
// same picker; the 7-vs-10 character length is what tells them apart.
function recentMonths(count: number): string[] {
  const now = new Date()
  const months: string[] = []
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  return months
}

const isMonthPeriod = (period: string) => period.length === 7

// Fixed windows Gina asks for by name. They're sentinel values in the picker
// that resolve to a plain month or day list, so the backend keeps seeing
// period-shaped selections and needs no preset concept of its own.
const CHANNEL_PRESETS = [
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'lastyear', label: 'Last Year' },
] as const
type ChannelPreset = (typeof CHANNEL_PRESETS)[number]['value']

const isPreset = (period: string | undefined): period is ChannelPreset =>
  CHANNEL_PRESETS.some((p) => p.value === period)

const presetLabel = (preset: ChannelPreset) =>
  CHANNEL_PRESETS.find((p) => p.value === preset)!.label

function presetPeriods(preset: ChannelPreset): string[] {
  const now = new Date()
  // Last 30 Days is DAYS, not weeks: it ends today rather than on a Monday
  // boundary, so a week list would over- or under-shoot at both ends.
  if (preset === 'last30') {
    const days: string[] = []
    for (let i = 29; i >= 0; i--) {
      days.push(isoDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() - i)))
    }
    return days
  }
  const year = preset === 'ytd' ? now.getFullYear() : now.getFullYear() - 1
  const lastMonth = preset === 'ytd' ? now.getMonth() + 1 : 12
  const months: string[] = []
  for (let m = 1; m <= lastMonth; m++) months.push(`${year}-${String(m).padStart(2, '0')}`)
  return months
}

function SortIcon({ sorted }: { sorted: false | 'asc' | 'desc' }) {
  if (sorted === 'asc') return <ChevronUp className="inline h-3 w-3 ml-0.5 opacity-60" />
  if (sorted === 'desc') return <ChevronDown className="inline h-3 w-3 ml-0.5 opacity-60" />
  return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 opacity-30" />
}

// Stable default for the events list while it has no data (loading, failed).
// A fresh `[]` each render would change scopeEvents every render, and the
// scope-repair effect below would set state forever.
const NO_EVENTS: EventResponse[] = []

export default function EventsAnalysis() {
  const { data: locations = [] } = useEventLocations()
  const { data: allEvents = NO_EVENTS } = useEvents()
  // `?event=<id>` (e.g. the Intelligence "Offline" link) seeds the initial
  // venue + week once; it isn't kept in sync after that.
  const [searchParams] = useSearchParams()
  const deepLinkWeek = useRef<number | null>(null)

  // 'all' = every venue, for questions like "all Dressage weeks anywhere".
  // 'brewster' / 'online' leave event mode entirely for a sales channel.
  const [venueId, setVenueId] = useState<
    number | 'all' | 'brewster' | 'online' | typeof ALL_LOCATIONS_VALUE | undefined
  >()
  // 'all' = every matching week in scope; a number picks one week.
  const [weekSel, setWeekSel] = useState<number | 'all' | undefined>()
  // Channel mode picks calendar periods — an ISO Monday week or a 'YYYY-MM'
  // month — instead of event ids.
  const [channelPeriodSel, setChannelPeriodSel] = useState<string | 'all' | undefined>()
  const [channelExtraPeriods, setChannelExtraPeriods] = useState<string[]>([])
  // Which EVENT weeks are in scope (not the product tag filter below).
  const [eventDiscipline, setEventDiscipline] = useState('')
  // Additional weeks hand-picked into a combined view.
  const [extraIds, setExtraIds] = useState<number[]>([])
  const [discipline, setDiscipline] = useState('')
  const [productType, setProductType] = useState('')
  const [vendor, setVendor] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [expandedProduct, setExpandedProduct] = useState<AnalysisProduct | null>(null)
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  // Channel mode: everything event-shaped (event discipline, size curve,
  // transfer columns) drops out while this is non-null.
  const channel: EventChannel | null =
    venueId === 'brewster' || venueId === 'online'
      ? venueId
      : venueId === ALL_LOCATIONS_VALUE
        ? 'all'
        : null

  // The last 12 calendar weeks / 13 calendar months, newest first. Computed
  // once per mount — the page is not open long enough to cross a boundary
  // meaningfully.
  const channelWeeks = useMemo(() => recentWeekStarts(CHANNEL_WEEK_COUNT), [])
  const channelMonths = useMemo(() => recentMonths(CHANNEL_MONTH_COUNT), [])

  // Every week the venue + event-discipline picks put in scope, newest first.
  // The week dropdown lists these (unsynced ones included, so picking one can
  // still explain itself); only the ones with data feed "All weeks".
  const scopeEvents = useMemo(
    () =>
      allEvents
        .filter((e) => venueId === 'all' || e.shopify_location_id === venueId)
        .filter((e) => !eventDiscipline || e.discipline === eventDiscipline)
        .sort((a, b) => b.event_date.localeCompare(a.event_date)),
    [allEvents, venueId, eventDiscipline]
  )

  // Future events get synced_at stamped with 0 products (nothing has happened
  // yet), so "has data" means products_synced > 0, not just synced.
  const matchingEvents = useMemo(
    () => scopeEvents.filter((e) => e.products_synced > 0),
    [scopeEvents]
  )

  // Default: the deep-linked event's venue, else the first location with
  // synced sales data. An unknown ?event id falls through to the default.
  useEffect(() => {
    if (venueId == null && allEvents.length > 0) {
      const linked = allEvents.find((e) => String(e.id) === searchParams.get('event'))
      const synced = linked ?? allEvents.find((e) => e.products_synced > 0) ?? allEvents[0]
      // The week is picked by the scope repair below once the venue's weeks
      // are in scope — setting it here would be wiped by that same pass.
      if (linked) deepLinkWeek.current = linked.id
      setVenueId(synced.shopify_location_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEvents, venueId])

  // Repair the week pick whenever the scope moves under it. Across all venues
  // a single week is meaningless, so the scope is forced to "All weeks".
  // A channel has no event weeks at all, so the event pick is emptied and the
  // channel pick defaults to the week in progress (and resets on the way out,
  // so re-entering a channel always lands on the current week again).
  useEffect(() => {
    if (channel) {
      setWeekSel(undefined)
      setExtraIds([])
      setChannelPeriodSel((prev) => prev ?? channelWeeks[0])
    } else {
      setChannelPeriodSel(undefined)
      setChannelExtraPeriods([])
      if (venueId === 'all') {
        setWeekSel('all')
        setExtraIds([])
      } else if (scopeEvents.length === 0) {
        setWeekSel(undefined)
        setExtraIds([])
      } else if (weekSel !== 'all' && !scopeEvents.some((e) => e.id === weekSel)) {
        const linked = scopeEvents.find((e) => e.id === deepLinkWeek.current)
        deepLinkWeek.current = null
        const withData = scopeEvents.find((e) => e.products_synced > 0)
        setWeekSel((linked ?? withData ?? scopeEvents[0]).id)
        setExtraIds([])
      }
    }
    setExpandedCategories(new Set())
    setExpandedProduct(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeEvents, venueId])

  // First id is the primary event; the rest merge into the combined view.
  const queryIds = useMemo(() => {
    if (weekSel === 'all') return matchingEvents.map((e) => e.id)
    if (weekSel == null) return []
    return [weekSel, ...extraIds]
  }, [weekSel, matchingEvents, extraIds])

  // Mirrors queryIds for channel mode: 'all' pools every listed week. Extras
  // are always the same type as the primary pick — changing the primary clears
  // them and the combine menu only offers that type.
  const selectedPeriods = useMemo(() => {
    if (!channel) return []
    if (channelPeriodSel === 'all') return channelWeeks
    if (channelPeriodSel == null) return []
    if (isPreset(channelPeriodSel)) return presetPeriods(channelPeriodSel)
    return [channelPeriodSel, ...channelExtraPeriods]
  }, [channel, channelPeriodSel, channelExtraPeriods, channelWeeks])

  // Only the day preset needs telling apart — its periods look exactly like
  // week starts. Everything else the hook infers from the period shape.
  const channelPeriodKind = channelPeriodSel === 'last30' ? ('day' as const) : undefined

  const primaryEvent = allEvents.find((e) => e.id === queryIds[0])
  const primaryEventId = primaryEvent?.id

  // Offline sales are entered per event, so the panel only shows when exactly
  // one event week is in view. (The revenue line's offline share comes from
  // the analysis totals, so it covers combined weeks and filters too.)
  const offlineEventId = !channel && queryIds.length === 1 ? primaryEventId : undefined

  // Exactly one of the two analyses is enabled at a time.
  const { data: eventAnalysis, isLoading: eventLoading } = useEventAnalysis(
    channel ? [] : queryIds,
    {
      discipline: discipline || undefined,
      product_type: productType || undefined,
      vendor: vendor || undefined,
    }
  )
  const {
    data: channelAnalysis,
    isLoading: channelLoading,
    error: channelError,
  } = useChannelAnalysis(
    channel,
    selectedPeriods,
    {
      discipline: discipline || undefined,
      product_type: productType || undefined,
      vendor: vendor || undefined,
    },
    channelPeriodKind
  )
  const analysis = channel ? channelAnalysis : eventAnalysis
  const isLoading = channel ? channelLoading : eventLoading

  const { data: trend } = useWeeklyTrend(typeof venueId === 'number' ? venueId : undefined, {
    discipline: eventDiscipline || undefined,
  })
  const { data: sizeCurve } = useSizeCurve(
    channel ? undefined : primaryEventId,
    expandedProduct?.product_id ?? null
  )

  const venueScopeLabel =
    venueId === 'all'
      ? 'all venues'
      : locations.find((l) => l.location_id === venueId)?.name ?? primaryEvent?.location_name ?? ''
  const disciplineScopeLabel = eventDiscipline
    ? DISCIPLINE_LABELS[eventDiscipline] ?? eventDiscipline
    : 'all disciplines'
  const channelLabel = channel ? channelAnalysis?.channel_label ?? CHANNEL_LABELS[channel] : ''

  // How much stock history backs the channel span. Event mode is always
  // covered — its denominator comes from the pack list, not from snapshots.
  const stock = channel ? channelAnalysis?.stock : undefined
  const covered = channel ? stock?.covered === true : true
  // Uncovered spans blank out Avail/ST%/Missed; say why rather than leaving
  // Rachel to guess whether the store really sold from nothing.
  const uncoveredNote =
    channel && channel !== 'all' && stock && !covered
      ? stock.history_from
        ? `Available and sell-through are blank because store stock snapshots only go back to ${fmtDate(stock.history_from)}. Periods from that date on fill in as the nightly snapshot accrues.`
        : 'Available and sell-through are blank until the nightly store stock snapshot has run — they fill in from then on.'
      : ''
  // 'all' pools locations, so "snapshots don't go back far enough" is the wrong
  // reason for the blanks — there simply is no single stock pool to divide by.
  const allLocationsNote =
    channel === 'all'
      ? 'Sales across every location and channel. Stock metrics are per-location — pick Brewster Store or a venue to see Available and sell-through.'
      : ''
  // Brewster and Online sell the same shelves, so their two sell-through
  // numbers describe one pool twice.
  const sharedPoolNote =
    channel === 'brewster' || channel === 'online'
      ? "Brewster and Online sell from the same store stock pool — their percentages overlap and don't add."
      : ''
  const soldWithoutStock = channel ? channelAnalysis?.sold_without_stock ?? null : null
  const availTooltip = channel ? CHANNEL_COLUMN_TOOLTIPS.qty_available : undefined

  // The trend charts read whole-period totals; only the table honours these
  // three product filters. When one is on, the chart is captioned so the two
  // numbers can't be read as the same thing.
  const productFiltersActive = Boolean(discipline || productType || vendor)

  // Rollup sell-through only spans the products that sold, so a store-wide
  // total off it reads high. Turn divides by the whole shelf instead — the only
  // honest aggregate.
  // Turn is a whole-shelf metric and the shelf can't be facet-filtered (the
  // pool is store-wide), so a filtered view suppresses it rather than dividing
  // a filtered numerator by the unfiltered pool.
  const storeTurnPct =
    channel && channelAnalysis && stock?.covered && stock.pool_units > 0 && !productFiltersActive
      ? (channelAnalysis.totals.qty_sold / stock.pool_units) * 100
      : null
  const storeTurnTooltip =
    stock && stock.pool_units > 0
      ? `Units sold this period ÷ ${stock.pool_units} units the store had stocked at period start (${stock.pool_variants} variants). Turn measures the whole shelf; the per-item sell-through on each row measures only that item.`
      : undefined

  // Same seven columns either way — only the tooltips change, because the
  // stock columns mean the store pool rather than a pack list.
  const columns = channel
    ? SORT_COLUMNS.map((c) =>
        c.key === 'qty_available'
          ? { ...c, tooltip: availTooltip }
          : CHANNEL_COLUMN_TOOLTIPS[c.key]
            ? { ...c, tooltip: CHANNEL_COLUMN_TOOLTIPS[c.key] }
            : c
      )
    : SORT_COLUMNS

  function onSort(key: SortKey) {
    const first = key === 'name' ? 'asc' : 'desc'
    if (sortKey !== key) {
      setSortKey(key)
      setSortDir(first)
    } else if (sortDir === first) {
      setSortDir(first === 'desc' ? 'asc' : 'desc')
    } else {
      setSortKey(null)
    }
  }

  function sortLevel<T>(list: T[], val: (x: T) => string | number | null): T[] {
    if (!sortKey) return list
    const dir = sortDir === 'desc' ? -1 : 1
    return [...list].sort((a, b) => {
      const av = val(a)
      const bv = val(b)
      if (typeof av === 'string' || typeof bv === 'string') {
        return dir * String(av ?? '').localeCompare(String(bv ?? ''))
      }
      // Nulls (no margin, no sell-through) always sort to the end.
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      return dir * (av - bv)
    })
  }

  type LevelRow = {
    qty_available: number
    qty_sold: number
    sell_through_pct: number | null
    revenue: number
    margin_pct: number | null
    stockout_count: number
  }

  function levelVal(x: LevelRow, name: string): string | number | null {
    switch (sortKey) {
      case 'name': return name
      case 'margin_pct': return x.margin_pct
      case 'missed': return x.stockout_count
      case 'sell_through_pct': return x.sell_through_pct
      case 'qty_available': return x.qty_available
      case 'qty_sold': return x.qty_sold
      case 'revenue': return x.revenue
      default: return null
    }
  }

  function variantVal(v: AnalysisVariant): string | number | null {
    switch (sortKey) {
      case 'name': return v.variant_title || v.sku || String(v.variant_id)
      case 'margin_pct': return v.margin_pct
      case 'missed': return v.est_missed_revenue
      case 'sell_through_pct': return v.sell_through_pct
      case 'qty_available': return v.qty_available
      case 'qty_sold': return v.qty_sold
      case 'revenue': return v.revenue
      default: return null
    }
  }

  function toggleCategory(productTypeKey: string) {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(productTypeKey)) next.delete(productTypeKey)
      else next.add(productTypeKey)
      return next
    })
  }

  const selectCls =
    'h-7 border border-[var(--line-input)] bg-[var(--surface-card)] px-2 text-xs focus:outline-none focus:border-[var(--brand)]'

  // Stock-derived cells go blank rather than lying when the span predates the
  // snapshots that back them (channel mode only — see `covered`).
  const dash = <span className="text-[var(--mute)]">—</span>
  const availTitle = (r: { qty_carryover: number; qty_transferred: number }) =>
    !covered
      ? undefined
      : channel
        ? `${r.qty_carryover} on hand at start + ${r.qty_transferred} restocked during the period`
        : `${r.qty_carryover} carried over + ${r.qty_transferred} transferred`

  const channelWeekLabel = (w: string) =>
    `Week of ${fmtDate(w)}${w === channelWeeks[0] ? ' (current)' : ''}`
  const channelMonthLabel = (m: string) =>
    `${fmtMonth(m)}${m === channelMonths[0] ? ' (so far)' : ''}`
  const channelPeriodLabel = (p: string) =>
    isMonthPeriod(p) ? channelMonthLabel(p) : channelWeekLabel(p)

  // Weeks and months never merge into one view — the primary pick decides the
  // type and the combine menu offers only that type ('all' is a week pool).
  // A preset is a fixed window, so it isn't in either pool and can't combine.
  const channelPeriodPool =
    channelPeriodSel &&
    channelPeriodSel !== 'all' &&
    !isPreset(channelPeriodSel) &&
    isMonthPeriod(channelPeriodSel)
      ? channelMonths
      : channelWeeks
  const periodNoun = channelPeriodPool === channelMonths ? 'month' : 'week'

  // "Combine" works the same either way — event weeks or calendar periods.
  const combineOptions = channel
    ? channelPeriodPool
        .filter((p) => p !== channelPeriodSel)
        .map((p) => ({
          key: p,
          label: channelPeriodLabel(p),
          checked: channelExtraPeriods.includes(p),
          toggle: (on: boolean) =>
            setChannelExtraPeriods((prev) => (on ? [...prev, p] : prev.filter((x) => x !== p))),
        }))
    : scopeEvents
        .filter((e) => e.id !== weekSel)
        .map((e) => ({
          key: String(e.id),
          label:
            `${fmtDate(e.event_date)} · ${DISCIPLINE_LABELS[e.discipline] ?? e.discipline}` +
            (e.products_synced === 0 ? ' (no data)' : ''),
          checked: extraIds.includes(e.id),
          toggle: (on: boolean) =>
            setExtraIds((prev) => (on ? [...prev, e.id] : prev.filter((id) => id !== e.id))),
        }))
  const extraCount = channel ? channelExtraPeriods.length : extraIds.length
  const combineDisabled = channel
    ? channelPeriodSel == null || channelPeriodPool.length < 2
    : weekSel == null || scopeEvents.length < 2
  const showCombine = channel
    ? channelPeriodSel !== 'all' && !isPreset(channelPeriodSel)
    : weekSel !== 'all'
  const combineNoun = channel ? periodNoun : 'week'

  return (
    <div className="brand-theme flex flex-col h-screen bg-[var(--surface-page)] text-[var(--text-primary)]">
      <TopTabs />
      <div className="flex-1 overflow-auto p-4 max-w-6xl w-full mx-auto">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h1 className="text-base font-semibold">Events Analysis</h1>
          <div className="flex-1" />
          <EventsSubTabs />
        </div>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <select
            className={selectCls}
            value={venueId ?? ''}
            onChange={(e) => {
              const v = e.target.value
              setVenueId(
                v === 'all' || v === 'brewster' || v === 'online' || v === ALL_LOCATIONS_VALUE
                  ? v
                  : Number(v) || undefined
              )
              setExtraIds([])
            }}
            data-testid="analysis-location"
          >
            <option value="">— location —</option>
            <option value="all">All venues</option>
            {locations.map((l) => (
              <option key={l.location_id} value={l.location_id}>{l.name}</option>
            ))}
            {/* Not events — same sales drill-down, no pack list behind it. */}
            <optgroup label="Channels">
              <option value="brewster">{CHANNEL_LABELS.brewster}</option>
              <option value="online">{CHANNEL_LABELS.online}</option>
              <option value={ALL_LOCATIONS_VALUE}>{CHANNEL_LABELS.all}</option>
            </optgroup>
          </select>
          {/* Scopes which EVENT weeks are in play — nothing to scope for a channel. */}
          {!channel && (
            <select
              className={selectCls}
              value={eventDiscipline}
              onChange={(e) => {
                setEventDiscipline(e.target.value)
                setExtraIds([])
              }}
              data-testid="analysis-event-discipline"
            >
              <option value="">All events</option>
              {Object.entries(DISCIPLINE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          )}
          {channel ? (
            <select
              className={selectCls}
              value={channelPeriodSel ?? ''}
              onChange={(e) => {
                const v = e.target.value
                // Switching the primary pick drops the extras — that's what
                // keeps a combined view all-weeks or all-months.
                setChannelPeriodSel(v === 'all' ? 'all' : v || undefined)
                setChannelExtraPeriods([])
              }}
              data-testid="analysis-channel-week"
            >
              <option value="all">All weeks</option>
              {/* Fixed windows — they resolve to months or days, not to a
                  pick out of the two pools below, so they never combine. */}
              <optgroup label="Presets">
                {CHANNEL_PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </optgroup>
              <optgroup label="Weeks">
                {channelWeeks.map((w) => (
                  <option key={w} value={w}>{channelWeekLabel(w)}</option>
                ))}
              </optgroup>
              <optgroup label="Months">
                {channelMonths.map((m) => (
                  <option key={m} value={m}>{channelMonthLabel(m)}</option>
                ))}
              </optgroup>
            </select>
          ) : (
            <select
              className={selectCls}
              value={weekSel ?? ''}
              onChange={(e) => {
                const v = e.target.value
                setWeekSel(v === 'all' ? 'all' : Number(v) || undefined)
                setExtraIds([])
              }}
              data-testid="analysis-week"
            >
              <option value="all">All weeks</option>
              {/* A single week across venues is meaningless — only "All weeks". */}
              {venueId !== 'all' &&
                scopeEvents.map((e) => (
                  <option key={e.id} value={e.id}>
                    {fmtDate(e.event_date)} · {DISCIPLINE_LABELS[e.discipline] ?? e.discipline}
                    {e.synced_at ? '' : ' (not synced)'}
                  </option>
                ))}
            </select>
          )}
          {/* Redundant once every week is already in scope. */}
          {showCombine && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant={extraCount > 0 ? 'default' : 'outline'}
                  className="h-7 text-xs"
                  disabled={combineDisabled}
                  title={`Merge more ${combineNoun}s into one combined view`}
                >
                  <Layers className="mr-1 h-3 w-3" />
                  {extraCount > 0 ? `+${extraCount} ${combineNoun}(s)` : `Combine ${combineNoun}s`}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 max-h-[50vh] overflow-y-auto">
                <DropdownMenuLabel className="text-xs font-semibold text-[var(--mute)] uppercase tracking-[0.06em]">
                  Combine with…
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {combineOptions.map((o) => (
                  <DropdownMenuCheckboxItem
                    key={o.key}
                    checked={o.checked}
                    onCheckedChange={o.toggle}
                    onSelect={(ev) => ev.preventDefault()}
                    className="text-xs"
                  >
                    {o.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <select className={selectCls} value={discipline} onChange={(e) => setDiscipline(e.target.value)}>
            {DISCIPLINE_FILTERS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
          <select className={selectCls} value={productType} onChange={(e) => setProductType(e.target.value)}>
            <option value="">All product types</option>
            {analysis?.facets.product_types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className={selectCls} value={vendor} onChange={(e) => setVendor(e.target.value)}>
            <option value="">All brands</option>
            {analysis?.facets.vendors.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        {/* Location-scoped — meaningless once venues are pooled. */}
        {!channel && venueId !== 'all' && trend && (
          <WeeklyTrendChart
            weeks={trend.weeks.filter((w) => w.revenue > 0)}
            filtersActive={productFiltersActive}
            selectedEventIds={queryIds}
          />
        )}
        {channel && channelAnalysis && (
          <ChannelTrendChart
            trend={channelAnalysis.trend}
            label={channelLabel}
            periodType={channelAnalysis.period_type}
            filtersActive={productFiltersActive}
          />
        )}

        {isLoading ? (
          <div className="space-y-1">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        ) : !analysis || analysis.categories.length === 0 ? (
          <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] px-2 py-8 text-center text-xs text-[var(--mute)]">
            {channel ? (
              channelError ? (
                <span className="inline-flex items-center gap-1">
                  <RefreshCw className="h-3 w-3" />
                  {channelError instanceof Error
                    ? channelError.message
                    : `Couldn’t load ${channelLabel} sales.`}
                </span>
              ) : selectedPeriods.length === 0 ? (
                'Pick a period to see channel sales.'
              ) : (
                'No sales match the current filters for these periods.'
              )
            ) : queryIds.length === 0 ? (
              'No event weeks with sales data match this venue and discipline.'
            ) : !primaryEvent?.synced_at ? (
              <span className="inline-flex items-center gap-1">
                <RefreshCw className="h-3 w-3" />
                This event hasn&apos;t been synced from Shopify yet — run “Sync” in Event Admin after the event.
              </span>
            ) : primaryEvent.products_synced === 0 ? (
              'No transfers or sales recorded for this event week — it likely hasn’t happened yet. Re-sync in Event Admin after the event.'
            ) : (
              'No products match the current filters.'
            )}
          </div>
        ) : (
          <>
            {/* Scope line (combined weeks / channel) and notes sit outside the cards. */}
            {(channel || queryIds.length > 1) && (
              <div className="text-xs mb-1">
                {channel ? (
                  <span className="text-[var(--mute)]">
                    {/* A preset is one named window — "8 months combined"
                        would describe how it's fetched, not what it is. */}
                    <strong className="text-[var(--text-primary)]">
                      {isPreset(channelPeriodSel)
                        ? presetLabel(channelPeriodSel)
                        : `${selectedPeriods.length} ${periodNoun}${selectedPeriods.length === 1 ? '' : 's'} combined`}
                    </strong>
                    {' · '}{channelLabel}
                  </span>
                ) : (
                  <span className="text-[var(--mute)]">
                    <strong className="text-[var(--text-primary)]">{queryIds.length} weeks combined</strong>
                    {' · '}{disciplineScopeLabel} · {venueScopeLabel}
                  </span>
                )}
              </div>
            )}
            {/* Brought/Carryover come off a pack list — events only. Channel
                mode drops Available and Sell-through entirely: the rollup
                still spans only the products that sold at least one unit, so
                a store-wide total off it reads far too high. Store turn is
                the honest aggregate. */}
            <div
              className="flex flex-wrap border border-[var(--line-warm)] bg-[var(--surface-card)] mb-2"
              data-testid="analysis-metrics"
            >
              {!channel && (
                <MetricCard
                  label="Brought"
                  value={<span className="tabular-nums">{analysis.totals.qty_transferred}</span>}
                  delta={
                    <span className="tabular-nums" data-testid="analysis-brought-sub">
                      <span title="Estimated stock still at the location from the previous event week (previous leftover minus outbound transfers)">
                        +{analysis.totals.qty_carryover} carryover
                      </span>
                      {' · '}
                      <span title="Carryover + brought — the sell-through denominator">
                        {analysis.totals.qty_available} available
                      </span>
                    </span>
                  }
                />
              )}
              <MetricCard label="Sold" value={<span className="tabular-nums">{analysis.totals.qty_sold}</span>} />
              {!channel && (
                <MetricCard
                  label="Sell-through"
                  value={
                    analysis.totals.sell_through_pct == null ? (
                      <span className="text-[var(--mute)]">—</span>
                    ) : (
                      <span
                        className={`tabular-nums ${SELL_THROUGH_TEXT[sellThroughLevel(analysis.totals.sell_through_pct)]}`}
                        data-testid="analysis-sell-through"
                      >
                        {analysis.totals.sell_through_pct.toFixed(1)}%
                      </span>
                    )
                  }
                />
              )}
              {!channel ? (
                // totals.revenue already includes offline; split it back out.
                <MetricCard
                  label="Revenue"
                  value={<span className="tabular-nums">{money(analysis.totals.revenue)}</span>}
                  delta={
                    <span className="tabular-nums" data-testid="analysis-revenue">
                      {money(analysis.totals.revenue - (analysis.totals.offline_revenue ?? 0))} POS
                      {' + '}
                      {money(analysis.totals.offline_revenue ?? 0)} offline
                    </span>
                  }
                />
              ) : (
                <MetricCard
                  label="Revenue"
                  value={<span className="tabular-nums">{money(analysis.totals.revenue)}</span>}
                />
              )}
              {storeTurnPct != null && (
                <MetricCard
                  label="Store turn"
                  title={storeTurnTooltip}
                  value={<span className="tabular-nums">{storeTurnPct.toFixed(1)}%</span>}
                />
              )}
            </div>
            {/* Full contrast on its own line: muted grey inside the stat row
                read as one more stat and got skipped, so the dashes looked
                like a broken page instead of missing history. */}
            {(uncoveredNote || allLocationsNote || (soldWithoutStock && soldWithoutStock.units > 0) || sharedPoolNote) && (
              <div className="flex flex-col gap-1 text-xs mb-2">
                {uncoveredNote && <span className="text-[var(--text-primary)]">{uncoveredNote}</span>}
                {allLocationsNote && <span className="text-[var(--text-primary)]">{allLocationsNote}</span>}
                {soldWithoutStock && soldWithoutStock.units > 0 && (
                  <span className="text-[var(--text-primary)]">
                    {soldWithoutStock.units} units (est. {money(soldWithoutStock.revenue)}) sold with
                    no store stock — fulfilled from dropship, partner locations, or event stock.
                  </span>
                )}
                {sharedPoolNote && <span className="text-[var(--mute)]">{sharedPoolNote}</span>}
              </div>
            )}

            <div className="overflow-auto border border-[var(--line-warm)] bg-[var(--surface-card)]">
              <table className="text-xs w-full">
                <thead className="bg-[var(--surface-header)] border-b border-[var(--line-warm)]">
                  <tr className="text-left text-[var(--mute)]">
                    {columns.map((c) => (
                      <th
                        key={c.key}
                        className={`tbl-th ${c.numeric ? 'text-right' : ''}`}
                        title={c.tooltip}
                      >
                        <button
                          className="inline-flex items-center gap-0.5 hover:text-[var(--text-primary)]"
                          onClick={() => onSort(c.key)}
                        >
                          {c.label}
                          <SortIcon sorted={sortKey === c.key ? sortDir : false} />
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortLevel(analysis.categories, (c) => levelVal(c, c.product_type)).map((cat, catIdx) => {
                    const catOpen = expandedCategories.has(cat.product_type)
                    // Index-based zebra on category rows only, so an expanded
                    // category's child rows don't flip the stripes below it.
                    return [
                      <tr
                        key={cat.product_type}
                        className={`border-b border-[var(--line-warm)] cursor-pointer hover:bg-[var(--surface-header)] font-semibold ${catIdx % 2 === 1 ? 'bg-[var(--surface-zebra)]' : ''}`}
                        onClick={() => toggleCategory(cat.product_type)}
                      >
                        <td className="px-2 py-1">
                          <span className="inline-flex items-center gap-1">
                            {catOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                            {cat.product_type}
                          </span>
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums" title={availTitle(cat)}>
                          {covered ? cat.qty_available : dash}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {cat.qty_sold}
                          <OfflineQty qty={cat.offline_qty} />
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {covered ? <SellThrough pct={cat.sell_through_pct} /> : dash}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{money(cat.revenue)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          <MarginCell pct={cat.margin_pct} avg={analysis.totals.margin_pct} />
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums text-[var(--mute)]">
                          {!covered ? dash : cat.stockout_count > 0 ? <SoldOutPill count={cat.stockout_count} /> : ''}
                        </td>
                      </tr>,
                      ...(catOpen
                        ? sortLevel(cat.products, (p) => levelVal(p, p.product_title)).map((prod) => {
                            const prodKey = `${cat.product_type}:${prod.product_id ?? prod.product_title}`
                            // "Custom (offline)" rows have no variants to open.
                            const expandable = prod.variants.length > 0
                            const prodOpen =
                              expandedProduct != null &&
                              `${cat.product_type}:${expandedProduct.product_id ?? expandedProduct.product_title}` === prodKey
                            return [
                              <tr
                                key={prodKey}
                                className={`border-b border-[var(--line-warm)] ${expandable ? 'cursor-pointer hover:bg-[var(--surface-header)]' : ''}`}
                                onClick={expandable ? () => setExpandedProduct(prodOpen ? null : prod) : undefined}
                              >
                                <td className={`px-2 py-1 ${expandable ? 'pl-7' : 'pl-11'}`}>
                                  <span className="inline-flex items-center gap-1">
                                    {expandable &&
                                      (prodOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />)}
                                    {prod.product_title}
                                    {prod.vendor && <span className="text-[var(--mute)]">· {prod.vendor}</span>}
                                  </span>
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums" title={availTitle(prod)}>
                                  {covered ? prod.qty_available : dash}
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums">
                                  {prod.qty_sold}
                                  <OfflineQty qty={prod.offline_qty} />
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums">
                                  {covered ? <SellThrough pct={prod.sell_through_pct} /> : dash}
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums">{money(prod.revenue)}</td>
                                <td className="px-2 py-1 text-right tabular-nums">
                                  <MarginCell pct={prod.margin_pct} avg={analysis.totals.margin_pct} />
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums text-[var(--mute)]">
                                  {!covered ? dash : prod.stockout_count > 0 ? <SoldOutPill count={prod.stockout_count} /> : ''}
                                </td>
                              </tr>,
                              ...(prodOpen
                                ? [
                                    ...sortLevel(prod.variants, variantVal).map((v: AnalysisVariant) => (
                                      <tr key={`v-${v.variant_id}`} className="border-b border-[var(--line-warm)] bg-[var(--surface-page)]">
                                        <td className="px-2 py-1 pl-14">
                                          {v.variant_title || v.sku || v.variant_id}
                                        </td>
                                        <td
                                          className="px-2 py-1 text-right tabular-nums"
                                          title={availTitle(v)}
                                        >
                                          {covered ? v.qty_available : dash}
                                          {/* The pack list didn't bring it all — some was already there. */}
                                          {!channel && v.qty_carryover > 0 && (
                                            <span className="text-[var(--mute)]">*</span>
                                          )}
                                        </td>
                                        <td className="px-2 py-1 text-right tabular-nums">
                                          {v.qty_sold}
                                          <OfflineQty qty={v.offline_qty} />
                                        </td>
                                        <td className="px-2 py-1 text-right tabular-nums">
                                          {covered ? (
                                            <SellThrough pct={v.sell_through_pct} />
                                          ) : (
                                            dash
                                          )}
                                        </td>
                                        <td className="px-2 py-1 text-right tabular-nums">{money(v.revenue)}</td>
                                        <td className="px-2 py-1 text-right tabular-nums">
                                          <MarginCell pct={v.margin_pct} avg={analysis.totals.margin_pct} />
                                        </td>
                                        <td className="px-2 py-1 text-right tabular-nums">
                                          {!covered ? (
                                            dash
                                          ) : (
                                            <span className="inline-flex items-center justify-end gap-1 whitespace-nowrap">
                                              {v.est_missed_revenue != null ? money(v.est_missed_revenue) : ''}
                                              {v.stockout_flag && <SoldOutPill />}
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    )),
                                    !channel && sizeCurve && sizeCurve.sizes.length > 0 && (
                                      <tr key="size-curve" className="border-b border-[var(--line-warm)]">
                                        <td colSpan={columns.length} className="px-2 py-2">
                                          <div className="text-xs">
                                            <div className="font-semibold mb-1">
                                              Size curve — all {DISCIPLINE_LABELS[primaryEvent?.discipline ?? ''] ?? ''} events at this location
                                            </div>
                                            <div className="flex gap-4 flex-wrap">
                                              {sizeCurve.sizes.map((s) => (
                                                <span key={s.size} className="inline-flex items-center gap-1">
                                                  <strong>{s.size}</strong>
                                                  <SellThrough pct={s.sell_through_pct} />
                                                  <span className="text-[var(--mute)]">
                                                    ({s.qty_sold}/{s.qty_available}
                                                    {s.stockout_count > 0 ? `, ${s.stockout_count}⚠` : ''})
                                                  </span>
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    ),
                                  ].filter(Boolean)
                                : []),
                            ]
                          })
                        : []),
                    ]
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Outside the analysis branch: an event with no POS data can still
            have offline sales to record. */}
        {offlineEventId != null && (
          <OfflineSalesPanel
            eventId={offlineEventId}
            productTypes={eventAnalysis?.facets.product_types ?? []}
          />
        )}
      </div>
    </div>
  )
}
